/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import 'src/services/communication/twilio'
import Layout from 'src/layouts/Layout'
import { useSettings } from 'src/hooks/useSettings'
import { useAccountAction, useAccountSelect } from 'src/hooks/useAccount'
import { useAppSelect } from 'src/hooks/useApp'
import VerticalAppBarContent from './components/vertical/AppBarContent'
import { useAppAction } from 'src/hooks/useApp'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import { useStorageSettingsAction } from 'src/hooks/useStorageSettings'

interface Props {
  contentHeightFixed?: boolean
}

const UserLayout = ({ contentHeightFixed }: Props) => {
  const navigate = useNavigate()
  const isAfterLogin = useAccountSelect.isAfterLogin()
  const isUser = useAccountSelect.isUser()
  const getProfile = useAccountAction.getProfile()
  const getShemas = useAppAction.getSchemas()
  const getSettings = useStorageSettingsAction.getSettings()
  const getSettingsStatus = useStorageSettingsAction.getSettings.status()
  const realtimeConnection = useAppAction.realtimeConnection()
  const getShemasStatus = useAppAction.getSchemas.status()
  const appSchemas = useAppSelect.schemas()
  const [fulfiledSchemas, setFulfiledSchemas] = React.useState(
    appSchemas?.length > 0
  )
  const { setPathQuery } = useSearchQuery()

  const getRefreshToken = useAccountAction.getRefreshToken()
  const getRefreshTokenStatus = useAccountAction.getRefreshToken.status()
  const isRefreshedToken = !isAfterLogin
    ? !getRefreshTokenStatus.isClear && !getRefreshTokenStatus.isPending
    : true

  React.useEffect(() => {
    if (!fulfiledSchemas && getShemasStatus.isFulfilled) {
      setFulfiledSchemas(true)
    }
  }, [fulfiledSchemas, getShemasStatus.isFulfilled])

  React.useEffect(() => {
    if (!isAfterLogin) {
      getRefreshToken()
    }
  }, [isAfterLogin])

  React.useEffect(() => {
    if (getSettingsStatus.isClear) {
      getSettings({})
    }
  }, [getSettingsStatus.isClear])

  React.useEffect(() => {
    if (isUser) {
      if (isRefreshedToken) {
        realtimeConnection()
        getShemas({ perPage: 10 })
        getProfile()
      }
    } else {
      const params = new URLSearchParams(location.search)
      const identifier = params.get('identifier')
      const resetToken = params.get('reset_token')

      setPathQuery('/login', {
        ...(resetToken ? { identifier, resetToken } : {}),
      })
    }
  }, [isUser, getProfile, isRefreshedToken])

  const { settings, saveSettings } = useSettings()

  const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const isLoading =
    (!isAfterLogin ? getRefreshTokenStatus.isPending : false) ||
    !fulfiledSchemas

  if (isLoading)
    return (
      <Stack
        sx={{
          flex: 1,
          display: 'flex',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Stack>
    )

  return (
    <Layout
      hidden={hidden}
      settings={settings}
      saveSettings={saveSettings}
      contentHeightFixed={contentHeightFixed}
      verticalLayoutProps={{
        appBar: {
          content: (props: { pathggleNavVisibility: any }) => (
            <VerticalAppBarContent
              hidden={hidden}
              settings={settings}
              saveSettings={saveSettings}
              toggleNavVisibility={props.pathggleNavVisibility}
            />
          ),
        },
      }}
    >
      <Stack mx={{ xs: 3, xl: 6 }} sx={{ height: '100%' }} flex={1}>
        <Outlet />
      </Stack>
    </Layout>
  )
}

export default UserLayout
